import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router";
import { db } from "../../../../config/firebase";
import { doc, collection, getDocs, getDoc, updateDoc, setDoc } from "firebase/firestore";
import { STEP_CONFIGS, StepDifficulty, StepRole } from "../../../../app/pages/dashboards/steps/stepConfig";
import { STEP_DEPENDENCIES, stepDependencyMap } from "../../../../config/stepDependencies";
import store from "../../../../setup/redux/Store";
import { writeTeamActivity } from "../../../../config/teamActivity";
import { writeNotification } from "../../../../hooks/useTeamNotifications";

// Step difficulty lookup: stepNumber → difficulty
const stepDifficultyMap: Record<number, StepDifficulty> = STEP_CONFIGS.reduce(
  (acc, s) => ({ ...acc, [s.stepNumber]: s.difficulty }),
  {} as Record<number, StepDifficulty>
);

// Step time lookup: stepNumber → estimatedTime string
const stepTimeMap: Record<number, string> = STEP_CONFIGS.reduce(
  (acc, s) => ({ ...acc, [s.stepNumber]: s.estimatedTime }),
  {} as Record<number, string>
);

// Step role lookup: stepNumber → defaultRole
const stepRoleMap: Record<number, StepRole> = STEP_CONFIGS.reduce(
  (acc, s) => ({ ...acc, [s.stepNumber]: s.defaultRole }),
  {} as Record<number, StepRole>
);

const difficultyStyle: Record<StepDifficulty, { bg: string; color: string; label: string }> = {
  easy:   { bg: "#D1FAE5", color: "#10B981", label: "Easy" },
  medium: { bg: "#FEF3C7", color: "#F59E0B", label: "Medium" },
  hard:   { bg: "#FEE2E2", color: "#EF4444", label: "Hard" },
};

const DifficultyBadge: React.FC<{ stepNum: number }> = ({ stepNum }) => {
  const diff = stepDifficultyMap[stepNum];
  if (!diff) return null;
  const { color, label } = difficultyStyle[diff];
  return (
    <span
      className="d-none d-sm-inline"
      style={{
        fontSize: "11px",
        fontWeight: 400,
        color,
        marginLeft: "8px",
        whiteSpace: "nowrap",
        flexShrink: 0,
        opacity: 0.75,
      }}
    >
      {label}
    </span>
  );
};

const TimeBadge: React.FC<{ stepNum: number }> = ({ stepNum }) => {
  const time = stepTimeMap[stepNum];
  if (!time) return null;
  return (
    <span
      className="d-none d-md-inline"
      style={{
        fontSize: "11px",
        fontWeight: 400,
        color: "#94A3B8",
        marginLeft: "6px",
        whiteSpace: "nowrap",
        flexShrink: 0,
        opacity: 0.75,
      }}
    >
      · {time}
    </span>
  );
};

// Role colours: Strategy = blue (#3B82F6), Execution = green (#10B981)
const roleStyle: Record<StepRole, { bg: string; color: string; label: string }> = {
  strategist: { bg: "#DBEAFE", color: "#1D4ED8", label: "Strategy" },
  executor:   { bg: "#D1FAE5", color: "#065F46", label: "Execution" },
};

const RoleBadge: React.FC<{ role: StepRole }> = ({ role }) => {
  const { bg, color, label } = roleStyle[role];
  return (
    <span
      className="d-none d-sm-inline-block"
      style={{
        fontSize: "10px",
        fontWeight: 500,
        color,
        backgroundColor: bg,
        padding: "2px 6px",
        borderRadius: "4px",
        marginLeft: "4px",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {label}
    </span>
  );
};

/**
 * DESIGN.md spec: "Score animation: 600ms ease-out — Score numbers counting up on load"
 * Animates from the previous value to the new target using a cubic ease-out curve.
 */
const useCountUp = (target: number, duration = 600): number => {
  const [display, setDisplay] = useState(0);
  const prevRef = useRef(0);

  useEffect(() => {
    const from = prevRef.current;
    prevRef.current = target;
    if (from === target) return;

    const diff = target - from;
    const startTime = performance.now();
    let animFrame: number;

    const step = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      // cubic ease-out: 1 - (1-t)^3
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(from + diff * eased));
      if (t < 1) animFrame = requestAnimationFrame(step);
    };

    animFrame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animFrame);
  }, [target, duration]);

  return display;
};

/** Wraps a score number in a count-up animation (must be a component, not a hook in .map) */
const AnimatedScore: React.FC<{ score: number }> = ({ score }) => {
  const display = useCountUp(score);
  return <>{display}</>;
};

interface CategoryScores {
  marketOpportunity: number;
  customerUnderstanding: number;
  valueAndProduct: number;
  businessModel: number;
  validationExecution: number;
}

interface CategoryReasons {
  marketOpportunity?: string;
  customerUnderstanding?: string;
  valueAndProduct?: string;
  businessModel?: string;
  validationExecution?: string;
}

type Props = {
  className: string;
  innerPadding?: string;
  userID: string;
  categoryScores?: CategoryScores;
  categoryReasons?: CategoryReasons;
};

// DESIGN.md category colours
const categoryConfig = [
  {
    id: "marketOpportunity",
    label: "Market Opportunity",
    shortLabel: "Market",
    icon: "bi-graph-up-arrow",
    catColor: "#3B82F6",
    catColorLight: "#DBEAFE",
    description: "Identify and validate your target market",
    steps: [1, 2, 3, 4, 5],
  },
  {
    id: "customerUnderstanding",
    label: "Customer Understanding",
    shortLabel: "Customer",
    icon: "bi-people",
    catColor: "#8B5CF6",
    catColorLight: "#EDE9FE",
    description: "Deep dive into your customer needs and behaviors",
    steps: [6, 7, 8, 9, 10],
  },
  {
    id: "valueAndProduct",
    label: "Value & Product",
    shortLabel: "Value",
    icon: "bi-box-seam",
    catColor: "#EC4899",
    catColorLight: "#FCE7F3",
    description: "Define your value proposition and product",
    steps: [11, 12, 13, 14],
  },
  {
    id: "businessModel",
    label: "Business Model",
    shortLabel: "Business",
    icon: "bi-currency-dollar",
    catColor: "#F59E0B",
    catColorLight: "#FEF3C7",
    description: "Build a sustainable business model",
    steps: [15, 16, 17, 18, 19],
  },
  {
    id: "validationExecution",
    label: "Validation & Execution",
    shortLabel: "Validation",
    icon: "bi-check2-circle",
    catColor: "#10B981",
    catColorLight: "#D1FAE5",
    description: "Test assumptions and plan your launch",
    steps: [20, 21, 22, 23, 24],
  },
];

// Canonical 24-step titles
const stepTitles: Record<number, string> = {
  1: "Market Segmentation",
  2: "Select a Beachhead Market",
  3: "Build an End User Profile",
  4: "Calculate TAM for Beachhead",
  5: "Profile the Persona",
  6: "Full Life Cycle Use Case",
  7: "High-Level Product Specification",
  8: "Quantify the Value Proposition",
  9: "Identify Next 10 Customers",
  10: "Define Your Core",
  11: "Chart Competitive Position",
  12: "Determine Customer's DMU",
  13: "Map Process to Acquire Customer",
  14: "Calculate TAM for Follow-on",
  15: "Design a Business Model",
  16: "Set Your Pricing Framework",
  17: "Calculate LTV",
  18: "Map the Sales Process",
  19: "Calculate COCA",
  20: "Identify Key Assumptions",
  21: "Test Key Assumptions",
  22: "Define the MVP",
  23: "Show Dogs Will Eat the Food",
  24: "Develop a Product Plan",
};

// DESIGN.md score colours
const getScoreHex = (score: number): string => {
  if (score >= 70) return "#10B981";
  if (score >= 40) return "#F59E0B";
  return "#EF4444";
};

const getScoreBgHex = (score: number): string => {
  if (score >= 70) return "#D1FAE5";
  if (score >= 40) return "#FEF3C7";
  return "#FEE2E2";
};

const getScoreLabel = (score: number): string => {
  if (score >= 70) return "Strong";
  if (score >= 40) return "Developing";
  return "Needs Work";
};

// Step assignment data stored per step key (e.g. "step1")
interface StepAssignmentData {
  assignedTo?: string | null;
  assignedRole?: StepRole | null;
  assignedToEmail?: string | null;
}

const StepsList: React.FC<Props> = ({
  className,
  innerPadding = "",
  userID,
  categoryScores,
  categoryReasons,
}) => {
  const location = useLocation();
  const { projectTitle, projectID } = location.state as {
    projectTitle: string;
    projectID: string;
  };
  // Current logged-in user (may differ from project owner userID when viewing as collaborator)
  const currentUID: string | null = store.getState().auth?.user?.uid ?? null;
  const isOwner = currentUID === userID;

  const [data, setData] = useState<Record<string, number>>({});
  // Steps whose blocking dependency was completed within the last 24h
  const [recentlyUnblockedSteps, setRecentlyUnblockedSteps] = useState<Set<number>>(new Set());
  // Tracks whether notifications have been written this session (avoid duplicate writes on re-renders)
  const notificationsWrittenRef = useRef(false);
  const [stepAssignments, setStepAssignments] = useState<Record<string, StepAssignmentData>>({});
  const [collaboratorMap, setCollaboratorMap] = useState<Record<string, string>>({}); // uid → email
  const [currentUserRole, setCurrentUserRole] = useState<StepRole | null>(null);
  const [isTeamProject, setIsTeamProject] = useState(false);
  const [teamId, setTeamId] = useState<string | null>(null); // for activity writes
  // savingStep tracks which step is being reassigned
  const [savingStep, setSavingStep] = useState<string | null>(null);
  // activeFilter: default "my_steps" for non-owner team members, "all" otherwise
  const [activeFilter, setActiveFilter] = useState<"all" | "my_steps" | "strategy" | "execution">("all");
  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({
    marketOpportunity: true,
    customerUnderstanding: false,
    valueAndProduct: false,
    businessModel: false,
    validationExecution: false,
  });

  const fetchAllStepsProgress = useCallback(async (uid: string, pid: string) => {
    const userRef = doc(db, "users", uid);
    const thisProjectRef = doc(userRef, "projects", pid);

    // Read project doc for team info
    const projectSnap = await getDoc(thisProjectRef);
    if (projectSnap.exists()) {
      const pd = projectSnap.data();
      const collaborators: Array<{ uid: string; email: string; role: string }> = pd.collaborators || [];
      if (collaborators.length > 0 || pd.teamId) {
        setIsTeamProject(true);
        setTeamId((pd.teamId as string) || null);
        // Build uid → email map
        const cMap: Record<string, string> = {};
        collaborators.forEach((c) => { cMap[c.uid] = c.email; });
        setCollaboratorMap(cMap);
        // Find current user's role in collaborators
        const me = collaborators.find((c) => c.uid === currentUID);
        if (me && (me.role === "strategist" || me.role === "executor")) {
          setCurrentUserRole(me.role as StepRole);
        }
        // Default filter to "my_steps" for non-owner team members
        if (!isOwner && me) {
          setActiveFilter("my_steps");
        }
      }
    }

    // Read all step progress docs
    const stepsStartedSnapshot = await getDocs(
      collection(thisProjectRef, "progress")
    );
    const steps: Record<string, number> = {};
    const updatedAtMap: Record<string, string> = {};
    const assignments: Record<string, StepAssignmentData> = {};
    stepsStartedSnapshot.docs.forEach((docSnapshot) => {
      const d = docSnapshot.data();
      const stepID = docSnapshot.id;
      steps[stepID] = d.stepProgress || 0;
      if (d.updatedAt) updatedAtMap[stepID] = d.updatedAt as string;
      if (d.assignedTo || d.assignedRole) {
        assignments[stepID] = {
          assignedTo: d.assignedTo ?? null,
          assignedRole: d.assignedRole ?? null,
          assignedToEmail: d.assignedToEmail ?? null,
        };
      }
    });
    setData(steps);
    setStepAssignments(assignments);

    // --- Dependency: recently-unblocked detection ---
    const now = Date.now();
    const oneDayAgo = now - 24 * 60 * 60 * 1000;
    const recentlyUnblocked = new Set<number>();
    STEP_DEPENDENCIES.forEach((dep) => {
      const allParentsComplete = dep.dependsOn.every(
        (parentStep) => (steps[`step${parentStep}`] || 0) >= 100
      );
      if (allParentsComplete) {
        const anyRecentlyCompleted = dep.dependsOn.some((parentStep) => {
          const updatedAt = updatedAtMap[`step${parentStep}`];
          if (!updatedAt) return false;
          return new Date(updatedAt).getTime() >= oneDayAgo;
        });
        if (anyRecentlyCompleted) recentlyUnblocked.add(dep.stepNumber);
      }
    });
    setRecentlyUnblockedSteps(recentlyUnblocked);

    // --- Dependency: write notifications for unblocked steps (owner only, once per session) ---
    const projectHasTeam = Object.keys(assignments).length > 0 || !!projectSnap.data()?.teamId;
    if (projectHasTeam && !notificationsWrittenRef.current) {
      notificationsWrittenRef.current = true;
      for (const dep of STEP_DEPENDENCIES) {
        const allParentsComplete = dep.dependsOn.every(
          (parentStep) => (steps[`step${parentStep}`] || 0) >= 100
        );
        if (allParentsComplete) {
          const assignment = assignments[`step${dep.stepNumber}`];
          const assignedUID = assignment?.assignedTo ?? null;
          const assignedEmail = assignment?.assignedToEmail ?? null;
          if (assignedUID) {
            try {
              const notifId = `unblocked_${dep.stepNumber}_from_${dep.dependsOn.join("_")}`;
              const notifRef = doc(db, "users", uid, "projects", pid, "notifications", notifId);
              await setDoc(
                notifRef,
                {
                  type: "stepUnblocked",
                  unlockedStepNumber: dep.stepNumber,
                  completedStepNumbers: dep.dependsOn,
                  assignedUID,
                  assignedEmail,
                  unlockedAt: new Date().toISOString(),
                },
                { merge: true }
              );
            } catch {
              // non-blocking — notification write failure should not affect UX
            }
            // Write global notification to the assigned member's notifications collection
            const stepConfig = STEP_CONFIGS.find((s) => s.stepNumber === dep.stepNumber);
            const parentTitle = dep.dependsOn
              .map((n) => STEP_CONFIGS.find((s) => s.stepNumber === n)?.pageTitle ?? `Step ${n}`)
              .join(", ");
            writeNotification(
              assignedUID,
              `handoff_ready_${projectID}_step${dep.stepNumber}`,
              {
                type: "handoff_ready",
                message: `Step ${dep.stepNumber}${stepConfig ? ` — ${stepConfig.pageTitle}` : ""} is now unblocked (${parentTitle} complete)`,
                read: false,
                createdAt: new Date().toISOString(),
                projectID: pid,
                stepNumber: dep.stepNumber,
                linkPath: `/project/${pid}/step/${dep.stepNumber}`,
              }
            );
          }
        }
      }
    }
  }, [currentUID, isOwner]);

  useEffect(() => {
    if (!userID) return;
    fetchAllStepsProgress(userID, projectID);
  }, [userID, projectID, fetchAllStepsProgress]);

  // Reassign a step to a team member (owner only)
  const handleReassign = useCallback(async (stepNum: number, targetUID: string, targetEmail: string) => {
    const stepKey = `step${stepNum}`;
    const role = targetUID === userID ? null : (stepRoleMap[stepNum] ?? null);
    setSavingStep(stepKey);
    try {
      const stepRef = doc(db, "users", userID, "projects", projectID, "progress", stepKey);
      await updateDoc(stepRef, {
        assignedTo: targetUID || null,
        assignedToEmail: targetEmail || null,
        assignedRole: role,
      });
      setStepAssignments((prev) => ({
        ...prev,
        [stepKey]: { assignedTo: targetUID || null, assignedToEmail: targetEmail || null, assignedRole: role },
      }));

      // Write team activity (fire-and-forget)
      if (teamId) {
        const currentUser = store.getState().auth?.user;
        const stepConfig = STEP_CONFIGS.find((s) => s.stepNumber === stepNum);
        writeTeamActivity(teamId, {
          type: "step_assigned",
          actorUID: userID,
          actorEmail: currentUser?.email ?? "",
          projectID,
          stepNumber: stepNum,
          stepTitle: stepConfig?.pageTitle,
          metadata: { assignedToEmail: targetEmail },
        });

        // Write global step_assigned notification to the newly assigned member
        if (targetUID && targetUID !== userID) {
          const currentUser2 = store.getState().auth?.user;
          const stepCfg = STEP_CONFIGS.find((s) => s.stepNumber === stepNum);
          const actorEmail = (currentUser2 as any)?.email ?? "";
          writeNotification(
            targetUID,
            `step_assigned_${projectID}_step${stepNum}`,
            {
              type: "step_assigned",
              message: `You've been assigned Step ${stepNum}${stepCfg ? ` — ${stepCfg.pageTitle}` : ""}`,
              read: false,
              createdAt: new Date().toISOString(),
              projectID,
              stepNumber: stepNum,
              actorEmail,
              linkPath: `/project/${projectID}/step/${stepNum}`,
            }
          );
        }
      }
    } catch {
      // non-blocking — step may not have a progress doc yet
    } finally {
      setSavingStep(null);
    }
  }, [userID, projectID, teamId]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const getCategoryProgress = (steps: number[]): number => {
    const stepsWithProgress = steps.filter(
      (step) => data[`step${step}`] !== undefined
    );
    if (stepsWithProgress.length === 0) return 0;
    const totalProgress = steps.reduce(
      (sum, step) => sum + (data[`step${step}`] || 0),
      0
    );
    return Math.round(totalProgress / steps.length);
  };

  const getCompletedSteps = (steps: number[]): number => {
    return steps.filter((step) => (data[`step${step}`] || 0) >= 100).length;
  };

  // Determines whether a step passes the active role filter
  const stepPassesFilter = (stepNum: number): boolean => {
    if (activeFilter === "all") return true;
    const stepKey = `step${stepNum}`;
    const assignment = stepAssignments[stepKey];
    if (activeFilter === "strategy") return stepRoleMap[stepNum] === "strategist";
    if (activeFilter === "execution") return stepRoleMap[stepNum] === "executor";
    // "my_steps": step is explicitly assigned to current user, or default role matches their team role
    if (activeFilter === "my_steps") {
      if (assignment?.assignedTo) return assignment.assignedTo === currentUID;
      if (currentUserRole) return stepRoleMap[stepNum] === currentUserRole;
      return true; // owner or unset: show all
    }
    return true;
  };

  // Returns true if the step is blocked by an incomplete dependency (team projects only)
  const isStepBlocked = useCallback((stepNum: number): boolean => {
    if (!isTeamProject) return false;
    const dep = stepDependencyMap[stepNum];
    if (!dep) return false;
    return dep.dependsOn.some((parentStep) => (data[`step${parentStep}`] || 0) < 100);
  }, [isTeamProject, data]);

  // Returns the first incomplete dependency step number, or null if none
  const getBlockingStep = useCallback((stepNum: number): number | null => {
    if (!isTeamProject) return null;
    const dep = stepDependencyMap[stepNum];
    if (!dep) return null;
    const blocker = dep.dependsOn.find((parentStep) => (data[`step${parentStep}`] || 0) < 100);
    return blocker ?? null;
  }, [isTeamProject, data]);

  // Filter chip styles
  const filterChip = (active: boolean) => ({
    fontSize: "12px",
    fontWeight: active ? 600 : 400,
    color: active ? "#1D4ED8" : "#64748B",
    background: active ? "#DBEAFE" : "#F8FAFC",
    border: `1px solid ${active ? "#BFDBFE" : "#E2E8F0"}`,
    borderRadius: "9999px",
    padding: "4px 12px",
    cursor: "pointer",
    whiteSpace: "nowrap" as const,
    transition: "all 0.15s ease-out",
  });

  return (
    <div
      className={`card ${className}`}
      style={{
        background: "#FFFFFF",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      }}
    >
      {/* Header */}
      <div className={`card-header border-0 pt-5 ${innerPadding}`} style={{ flexDirection: "column", alignItems: "flex-start" }}>
        <h3 className="card-title align-items-start flex-column">
          <span
            style={{ fontSize: "22px", fontWeight: 600, color: "#0F172A", lineHeight: "1.35", letterSpacing: "-0.01em" }}
          >
            24 Steps to Validation
          </span>
          <span className="mt-1" style={{ fontSize: "13px", color: "#475569" }}>
            Complete each category to validate your business idea
          </span>
        </h3>
        {/* Filter controls — only shown for team projects */}
        {isTeamProject && (
          <div className="d-flex align-items-center gap-2 mt-3 flex-wrap">
            {(["all", "my_steps", "strategy", "execution"] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setActiveFilter(f)}
                style={filterChip(activeFilter === f)}
              >
                {f === "all" ? "All" : f === "my_steps" ? "My Steps" : f === "strategy" ? "Strategy" : "Execution"}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="card-body pt-3">
        {/* 5 Category Score Cards — DESIGN.md score card pattern */}
        <div className="row g-3 mb-6">
          {categoryConfig.map((cat) => {
            const score = categoryScores
              ? categoryScores[cat.id as keyof CategoryScores]
              : getCategoryProgress(cat.steps);
            const completedSteps = getCompletedSteps(cat.steps);
            const scoreColor = getScoreHex(score);
            const scoreBg = getScoreBgHex(score);

            return (
              <div className="col-12 col-sm-6 col-xl" key={cat.id}>
                <div
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid #E2E8F0",
                    borderLeft: `4px solid ${cat.catColor}`,
                    borderRadius: "12px",
                    padding: "20px 16px",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                    height: "100%",
                  }}
                >
                  {/* Category label */}
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: 500,
                      color: "#94A3B8",
                      textTransform: "uppercase",
                      letterSpacing: "0.02em",
                      marginBottom: "6px",
                    }}
                  >
                    {cat.shortLabel}
                  </div>
                  {/* Score number — mono, 28px, score colour, count-up animation (DESIGN.md) */}
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: 700,
                      fontFamily: "JetBrains Mono, monospace",
                      color: scoreColor,
                      lineHeight: 1.1,
                      marginBottom: "8px",
                    }}
                  >
                    <AnimatedScore score={score} />
                    <span
                      style={{ fontSize: "14px", fontWeight: 400, color: "#94A3B8" }}
                    >
                      %
                    </span>
                  </div>
                  {/* Progress bar — 6px, category colour */}
                  <div
                    style={{
                      height: "6px",
                      background: "#F1F5F9",
                      borderRadius: "9999px",
                      overflow: "hidden",
                      marginBottom: "8px",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${score}%`,
                        background: cat.catColor,
                        borderRadius: "9999px",
                        transition: "width 0.4s ease-out",
                      }}
                    />
                  </div>
                  {/* Footer: steps done + status label */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <span style={{ fontSize: "11px", color: "#94A3B8" }}>
                      {completedSteps}/{cat.steps.length} done
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 500,
                        color: scoreColor,
                        background: scoreBg,
                        padding: "2px 8px",
                        borderRadius: "9999px",
                      }}
                    >
                      {getScoreLabel(score)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Accordion — Step list grouped by category */}
        <div>
          {categoryConfig.map((category) => {
            const categoryProgress = getCategoryProgress(category.steps);
            const completedSteps = getCompletedSteps(category.steps);
            const isExpanded = expandedCategories[category.id];
            const score = categoryScores
              ? categoryScores[category.id as keyof CategoryScores]
              : categoryProgress;
            const scoreColor = getScoreHex(score);

            return (
              <div key={category.id} className="mb-3">
                {/* Category Header — clickable, category colour */}
                <div
                  role="button"
                  tabIndex={0}
                  className="d-flex align-items-center p-4"
                  onClick={() => toggleCategory(category.id)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && toggleCategory(category.id)
                  }
                  style={{
                    background: isExpanded ? "#FFFFFF" : "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    borderLeft: `4px solid ${category.catColor}`,
                    borderRadius: "12px",
                    cursor: "pointer",
                    transition: "background 0.15s ease-out, box-shadow 0.15s ease-out",
                  }}
                >
                  {/* Category icon */}
                  <div
                    className="me-4"
                    style={{
                      width: "36px",
                      height: "36px",
                      background: category.catColorLight,
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <i
                      className={`bi ${category.icon}`}
                      style={{ color: category.catColor, fontSize: "16px" }}
                    />
                  </div>

                  {/* Category info */}
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-1 mb-1">
                      <span
                        style={{
                          fontSize: "15px",
                          fontWeight: 600,
                          color: "#0F172A",
                          minWidth: 0,
                        }}
                      >
                        {category.label}
                      </span>
                      <div className="d-flex align-items-center gap-3 flex-shrink-0">
                        <span
                          style={{
                            fontSize: "11px",
                            color: "#475569",
                            background: "rgba(255,255,255,0.7)",
                            padding: "3px 10px",
                            borderRadius: "9999px",
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {completedSteps}/{category.steps.length} steps
                        </span>
                        <span
                          style={{
                            fontSize: "15px",
                            fontWeight: 700,
                            fontFamily: "JetBrains Mono, monospace",
                            color: scoreColor,
                            minWidth: "42px",
                            textAlign: "right",
                          }}
                        >
                          <AnimatedScore score={score} />%
                        </span>
                      </div>
                    </div>

                    {/* Category progress bar */}
                    <div
                      style={{
                        height: "4px",
                        background: "rgba(255,255,255,0.5)",
                        borderRadius: "9999px",
                        overflow: "hidden",
                        maxWidth: "min(240px, 100%)",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${score}%`,
                          background: category.catColor,
                          borderRadius: "9999px",
                          transition: "width 0.4s ease-out",
                        }}
                      />
                    </div>

                    {/* AI reasoning (if available) */}
                    {categoryReasons &&
                      categoryReasons[category.id as keyof CategoryReasons] && (
                        <div
                          className="mt-1"
                          style={{ fontSize: "12px", color: "#475569" }}
                        >
                          <i
                            className="bi bi-robot me-1"
                            style={{ color: category.catColor }}
                          />
                          {categoryReasons[category.id as keyof CategoryReasons]}
                        </div>
                      )}
                  </div>

                  {/* Chevron */}
                  <div className="ms-4" style={{ flexShrink: 0 }}>
                    <i
                      className={`bi ${
                        isExpanded ? "bi-chevron-up" : "bi-chevron-down"
                      }`}
                      style={{ fontSize: "16px", color: "#475569" }}
                    />
                  </div>
                </div>

                {/* Steps list — collapsible */}
                {isExpanded && (
                  <div
                    className="mt-2 ps-4"
                    style={{
                      borderLeft: `2px solid ${category.catColor}44`,
                      marginLeft: "20px",
                    }}
                  >
                    {category.steps
                      .filter((stepNum) => stepPassesFilter(stepNum))
                      .map((stepNum, idx, filteredArr) => {
                      const stepProgress = data[`step${stepNum}`] || 0;
                      const isDone = stepProgress >= 100;
                      const isStarted = stepProgress > 0;
                      const isLast = idx === filteredArr.length - 1;
                      const stepKey = `step${stepNum}`;
                      const assignment = stepAssignments[stepKey];
                      const defaultRole = stepRoleMap[stepNum];
                      const assignedToEmail = assignment?.assignedToEmail || null;
                      const assignedToUID = assignment?.assignedTo || null;

                      // Build reassign options for owner: project owner + collaborators
                      const reassignOptions: Array<{ uid: string; email: string }> = isOwner
                        ? [
                            { uid: userID, email: "Me (owner)" },
                            ...Object.entries(collaboratorMap).map(([uid, email]) => ({ uid, email })),
                          ]
                        : [];

                      const blocked = isStepBlocked(stepNum);
                      const blockingStep = getBlockingStep(stepNum);
                      const isRecentlyUnblocked = recentlyUnblockedSteps.has(stepNum);

                      return (
                        <div
                          key={stepNum}
                          className="d-flex align-items-center py-3"
                          style={{
                            borderBottom: isLast
                              ? "none"
                              : "1px solid #F1F5F9",
                            opacity: blocked ? 0.65 : 1,
                          }}
                        >
                          {/* Completion indicator (or lock icon when blocked) */}
                          <div
                            className="me-3"
                            style={{ position: "relative", flexShrink: 0 }}
                          >
                            {blocked ? (
                              <div
                                title={blockingStep ? `Waiting on Step ${String(blockingStep).padStart(2, "0")}: ${stepTitles[blockingStep]}` : "Waiting on a dependency step"}
                                style={{
                                  width: "24px",
                                  height: "24px",
                                  borderRadius: "9999px",
                                  border: "2px solid #CBD5E1",
                                  background: "#F8FAFC",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "default",
                                }}
                              >
                                <i
                                  className="bi bi-lock-fill"
                                  style={{ color: "#94A3B8", fontSize: "10px" }}
                                />
                              </div>
                            ) : (
                              <div
                                style={{
                                  width: "24px",
                                  height: "24px",
                                  borderRadius: "9999px",
                                  border: isDone
                                    ? `2px solid ${category.catColor}`
                                    : isStarted
                                    ? `2px solid ${category.catColor}88`
                                    : "2px solid #E2E8F0",
                                  background: isDone ? category.catColor : "transparent",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.2s ease",
                                }}
                              >
                                {isDone && (
                                  <i
                                    className="bi bi-check"
                                    style={{
                                      color: "white",
                                      fontSize: "13px",
                                      fontWeight: 700,
                                    }}
                                  />
                                )}
                                {isStarted && !isDone && (
                                  <div
                                    style={{
                                      width: "8px",
                                      height: "8px",
                                      borderRadius: "9999px",
                                      background: category.catColor,
                                      opacity: 0.7,
                                    }}
                                  />
                                )}
                              </div>
                            )}
                            {/* Recently-unblocked notification dot */}
                            {isRecentlyUnblocked && !blocked && (
                              <span
                                title="Recently unlocked — a dependency was just completed"
                                style={{
                                  position: "absolute",
                                  top: "-3px",
                                  right: "-3px",
                                  width: "8px",
                                  height: "8px",
                                  borderRadius: "9999px",
                                  background: "#10B981",
                                  border: "1.5px solid #FFFFFF",
                                  display: "block",
                                }}
                              />
                            )}
                          </div>

                          {/* Step title + badges */}
                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                              <Link
                                to={`/step${stepNum}`}
                                state={{ projectTitle, projectID, userID }}
                                className="steps-list-link"
                                style={{
                                  fontSize: "14px",
                                  fontWeight: isDone ? 500 : 400,
                                  color: isDone ? "#0F172A" : "#475569",
                                  textDecoration: "none",
                                  minWidth: 0,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "11px",
                                    fontFamily: "JetBrains Mono, monospace",
                                    color: "#94A3B8",
                                    marginRight: "8px",
                                  }}
                                >
                                  {String(stepNum).padStart(2, "0")}
                                </span>
                                {stepTitles[stepNum]}
                              </Link>
                              <DifficultyBadge stepNum={stepNum} />
                              <TimeBadge stepNum={stepNum} />
                              {/* Role badge — only shown for team projects */}
                              {isTeamProject && defaultRole && <RoleBadge role={defaultRole} />}
                            </div>
                            {/* Assigned member row */}
                            {isTeamProject && (
                              <div className="d-flex align-items-center mt-1" style={{ gap: "6px" }}>
                                {assignedToEmail ? (
                                  <span style={{ fontSize: "11px", color: "#64748B" }}>
                                    <i className="bi bi-person-fill me-1" style={{ color: "#94A3B8" }} />
                                    {assignedToUID === userID ? "You (owner)" : assignedToEmail}
                                  </span>
                                ) : (
                                  <span style={{ fontSize: "11px", color: "#CBD5E1" }}>Unassigned</span>
                                )}
                                {/* Reassign dropdown — owner only */}
                                {isOwner && reassignOptions.length > 1 && (
                                  <div className="dropdown d-inline-block">
                                    <button
                                      type="button"
                                      className="dropdown-toggle"
                                      data-bs-toggle="dropdown"
                                      style={{
                                        fontSize: "10px",
                                        color: "#94A3B8",
                                        background: "none",
                                        border: "none",
                                        padding: "0 4px",
                                        cursor: "pointer",
                                      }}
                                      disabled={savingStep === stepKey}
                                    >
                                      {savingStep === stepKey ? (
                                        <span className="spinner-border spinner-border-sm" style={{ width: "10px", height: "10px" }} />
                                      ) : (
                                        <i className="bi bi-pencil" />
                                      )}
                                    </button>
                                    <ul className="dropdown-menu dropdown-menu-sm shadow-sm" style={{ fontSize: "13px", minWidth: "180px" }}>
                                      {reassignOptions.map((opt) => (
                                        <li key={opt.uid}>
                                          <button
                                            type="button"
                                            className="dropdown-item"
                                            onClick={() => handleReassign(stepNum, opt.uid, opt.email)}
                                          >
                                            {opt.email}
                                          </button>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Progress + Nav arrow */}
                          <div
                            className="d-flex align-items-center ms-3"
                            style={{ flexShrink: 0, gap: "12px" }}
                          >
                            {isStarted && (
                              <div className="d-none d-sm-flex" style={{ alignItems: "center", gap: "8px" }}>
                                <div
                                  style={{
                                    width: "80px",
                                    height: "4px",
                                    background: "#F1F5F9",
                                    borderRadius: "9999px",
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    style={{
                                      height: "100%",
                                      width: `${stepProgress}%`,
                                      background: isDone
                                        ? category.catColor
                                        : "#94A3B8",
                                      borderRadius: "9999px",
                                      transition: "width 0.4s ease-out",
                                    }}
                                  />
                                </div>
                                <span
                                  style={{
                                    fontSize: "11px",
                                    fontFamily: "JetBrains Mono, monospace",
                                    color: "#94A3B8",
                                    minWidth: "30px",
                                  }}
                                >
                                  {stepProgress}%
                                </span>
                              </div>
                            )}
                            <Link
                              to={`/step${stepNum}`}
                              state={{ projectTitle, projectID, userID }}
                              style={{
                                width: "28px",
                                height: "28px",
                                background: "#F8FAFC",
                                border: "1px solid #E2E8F0",
                                borderRadius: "8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                textDecoration: "none",
                                color: "#475569",
                                flexShrink: 0,
                                transition: "all 0.15s ease-out",
                              }}
                            >
                              <i
                                className="bi bi-arrow-right"
                                style={{ fontSize: "12px" }}
                              />
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        .steps-list-link:hover {
          color: #3B82F6 !important;
        }
      `}</style>
    </div>
  );
};

export { StepsList };
